import React from 'react';
import { colors } from '../../styles/tokens';
import { fmtValor } from '../../utils/scorecard';
import type { KpiScorecard } from '../../types/scorecard.types';

/**
 * Trazos de una serie con huecos: cada tramo continuo es un `M … L …` propio.
 * Un `null` CORTA la línea —nunca se une ni se pinta como cero—, igual que
 * `GraficoMeta`: una línea plana hacia una semana sin dato se lee como «dejaron
 * de vender».
 */
export function trazos(
  valores: (number | null)[],
  ancho: number,
  alto: number,
  max: number,
  min = 0,
): string {
  const n = valores.length;
  if (n === 0 || max <= min) return '';
  const x = (i: number) => (n === 1 ? ancho / 2 : (i / (n - 1)) * ancho);
  const y = (v: number) => alto - ((v - min) / (max - min)) * alto;
  let d = '';
  let abierto = false;
  valores.forEach((v, i) => {
    if (v === null || !Number.isFinite(v)) {
      abierto = false;
      return;
    }
    d += `${abierto ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)} `;
    abierto = true;
  });
  return d.trim();
}

export interface TendenciaSemanalProps {
  kpi: KpiScorecard;
  ancho?: number;
  alto?: number;
}

/**
 * Minigráfica de la tendencia de un KPI: el año en curso en el color de marca,
 * línea sólida; el año anterior en gris PUNTEADO (la segunda codificación: la
 * identidad no depende sólo del color). La línea actual termina en la semana
 * elegida, con un punto.
 */
const TendenciaSemanal: React.FC<TendenciaSemanalProps> = ({ kpi, ancho = 120, alto = 28 }) => {
  const actual = kpi.tendencia.map((t) => t.actual);
  const anterior = kpi.tendencia.map((t) => t.anterior);
  const todos = [...actual, ...anterior].filter((v): v is number => v !== null && Number.isFinite(v));
  const conDato = actual.filter((v) => v !== null).length;
  const ultimo = kpi.tendencia[kpi.tendencia.length - 1];
  const etiqueta =
    `Tendencia de ${kpi.rotulo}, ${kpi.tendencia.length} semanas: ` +
    (ultimo
      ? `semana ${ultimo.numero} ${fmtValor(ultimo.actual, kpi.unidad)}` +
        (ultimo.anterior !== null ? `, año anterior ${fmtValor(ultimo.anterior, kpi.unidad)}` : '')
      : 'sin datos');
  if (todos.length === 0 || conDato === 0) {
    return (
      <span role="img" aria-label={etiqueta} style={{ fontSize: 11, color: colors.texto.muted }}>
        sin serie
      </span>
    );
  }
  const max = Math.max(...todos);
  const min = Math.min(0, ...todos);
  const pad = 3;
  const w = ancho - pad * 2;
  const h = alto - pad * 2;
  const xUltimo = actual.length === 1 ? w / 2 : w;
  const vUltimo = actual[actual.length - 1];
  return (
    <svg
      role="img"
      aria-label={etiqueta}
      width={ancho}
      height={alto}
      viewBox={`0 0 ${ancho} ${alto}`}
      style={{ display: 'block', overflow: 'visible' }}
      data-testid={`tendencia-${kpi.clave}`}
    >
      <g transform={`translate(${pad},${pad})`}>
        <path
          d={trazos(anterior, w, h, max, min)}
          fill="none"
          stroke={colors.texto.tertiary}
          strokeWidth={1.5}
          strokeDasharray="3 3"
          vectorEffect="non-scaling-stroke"
        />
        <path
          d={trazos(actual, w, h, max, min)}
          fill="none"
          stroke={colors.brand}
          strokeWidth={2}
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
        {vUltimo !== null && (
          <circle
            cx={xUltimo}
            cy={h - ((vUltimo - min) / (max - min || 1)) * h}
            r={3}
            fill={colors.brand}
            stroke="#fff"
            strokeWidth={1.5}
          />
        )}
      </g>
    </svg>
  );
};

export default TendenciaSemanal;
